<template>
  <aside class="sidebar" :style="{ maxWidth: isSidebarCollapsed ? '50px' : '' }">
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
      class="page-alert-error"
    />
    <transition name="fade" mode="in-out">
      <div
        id="sidebar_logo_btn"
        class="holder-logo"
        :style="{ opacity: isSidebarCollapsed ? '0' : '1' }"
        @click="$router.push({ name: 'Home' })"
      >
        <a class="logo">
          <LogoIcon />
        </a>
      </div>
    </transition>
    <ul class="sidebar-list">
      <router-link
        v-if="hasPermission(['base_rights'])"
        id="sidebar_goto_dashboard_btn"
        :to="{ path: '/dashboard' }"
        tag="li"
      >
        <a id="main-menu-item-dashboard">
          <DashboardIcon />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Dashboard</span>
          </transition>
        </a>
      </router-link>
      <li
        v-if="hasPermission(['base_rights'])"
        :class="{'active': ($route.path === '/cases' || $route.path === '/create-case') && !$route.params.slug}"
      >
        <router-link
          id="sidebar_goto_cases_btn"
          tag="a"
          :to="{ path: '/cases' }"
        >
          <CaseIconFilled18 v-if="($route.path === '/cases' || $route.path === '/create-case')" />
          <CaseIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Case management</span>
          </transition>
        </router-link>
      </li>
      <router-link
        v-if="hasPermission(['view_contacts'])"
        id="sidebar_goto_team_btn"
        :to="{ path: '/team' }"
        tag="li"
      >
        <a id="main-menu-item-team">
          <TeamIconFilled18
            v-if="isActivePath('/team')
              && !isActivePath('/notifications/team')
              && !$route.path.includes('case-actions')"
          />
          <TeamIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Team</span>
          </transition>
        </a>
      </router-link>
      <router-link
        v-if="hasPermission(['invite_users'])"
        :to="{ path: '/invite-new-contact' }"
        tag="li"
      >
        <a id="main-menu-item-invite" href="">
          <InviteIconFilled18 v-if="isActivePath('/invite-new-contact')" />
          <InviteIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Invitations</span>
          </transition>
        </a>
      </router-link>
      <router-link
        v-if="hasPermission(['view_permissions'])"
        id="sidebar_goto_permissions_btn"
        :to="{ path: '/permissions/role' }"
        :class="{'active': isActivePath('/permissions')}"
        tag="li"
      >
        <a id="main-menu-item-permissions" href="">
          <PermissionsIconFilled18 v-if="isActivePath('/permissions')" />
          <PermissionsIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Permissions</span>
          </transition>
        </a>
      </router-link>
      <router-link
        v-if="hasPermission(['view_contacts'])"
        id="sidebar_goto_profile_user_btn"
        :to="{ path: '/management' }"
        tag="li"
      >
        <a>
          <ManagementIconFilled18 v-if="$route.path === '/management/custodians'" />
          <ManagementIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Manage Custodians</span>
          </transition>
        </a>
      </router-link>
    </ul>
    <div class="case-select-wrapper">
      <div
        class="case-select"
        :class="{ 'case-select-none' : isSidebarCollapsed}"
        @click="collapseSidebar(false)"
      >
        <CustomCaseSelect />
      </div>
    </div>
    <ul v-if="currentCase" class="sidebar-list">
      <router-link
        v-if="hasPermission(['view_case'])"
        id="sidebar_goto_case_profile_btn"
        :to="{ path: `/cases/${currentCase.id}/users` }"
        :class="{'active': $route.path === `/cases/${currentCase.id}/users` || $route.path === `/cases/${currentCase.id}/clients`}"
        tag="li"
      >
        <a id="case-menu-item-case_profile">
          <CaseProfileIconFilled18
            v-if="$route.path === `/cases/${currentCase.id}/users` || $route.path === `/cases/${currentCase.id}/clients`"
          />
          <CaseProfileIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Case Profile</span>
          </transition>
        </a>
      </router-link>
      <li
        v-if="hasPermission(['view_case'])"
        :class="{'active': isActivePath('/messages')}"
      >
        <router-link
          id="sidebar_goto_case_messages_btn"
          tag="a"
          :to="{ path: `/cases/${currentCase.id}/messages/chats` }"
        >
          <MessagesIconFilled18 v-if="isActivePath('/messages')" />
          <MessagesIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Messages</span>
          </transition>
        </router-link>
      </li>
      <router-link
        v-if="hasPermission(['download_case_file'])"
        id="sidebar_goto_team_btn"
        :to="{ path: `/cases/${currentCase.id}/files` }"
        tag="li"
      >
        <a id="main-menu-item-team">
          <FilesIconFilled18 v-if="isActivePath(`/cases/${currentCase.id}/files`)" />
          <FilesIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Files</span>
          </transition>
        </a>
      </router-link>
      <router-link
        v-if="hasPermission(['view_case'])"
        :to="{ path: `/cases/${currentCase.id}/case-actions` }"
        tag="li"
      >
        <a id="main-menu-item-invite" href="">
          <CaseActionsIconFilled18 v-if="isActivePath(`/cases/${currentCase.id}/case-actions`)" />
          <CaseActionsIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Case Actions</span>
          </transition>
        </a>
      </router-link>
      <router-link
        v-if="hasPermission(['add_custodian'])"
        id="sidebar_goto_case_custodians"
        :to="{ path: `/cases/${currentCase.id}/management/custodians` }"
        tag="li"
      >
        <a>
          <ManagementIconFilled18 v-if="isActivePath(`/cases/${currentCase.id}/management/custodians`)" />
          <ManagementIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Manage Custodians</span>
          </transition>
        </a>
      </router-link>
      <router-link
        v-if="hasPermission(['view_case'])"
        id="sidebar_goto_support_btn"
        :to="{ path: `/cases/${currentCase.id}/custodian-support` }"
        tag="li"
      >
        <a>
          <CustodianSupportIconFilled18 v-if="isActivePath(`/cases/${currentCase.id}/custodian-support`)" />
          <CustodianSupportIconHollow18 v-else />
          <transition name="fade" mode="in-out">
            <span v-show="!isSidebarCollapsed">Custodian Support</span>
          </transition>
        </a>
      </router-link>
    </ul>
    <div class="collapse-wrapper" @click="collapseSidebar">
      <CollapseNavigationIcon18 />
      <transition name="fade" mode="in-out">
        <span v-show="!isSidebarCollapsed">Collapse navigation</span>
      </transition>
    </div>
  </aside>
</template>

<script>
import hasPermission from '../utils/permission';
import LogoIcon from '@/components/icons/LogoIcon.vue';
import AlertErrorMsg from '@/components/profile/AlertErrorMsg.vue';
import CustomCaseSelect from '@/components/inputs/CustomCaseSelect.vue';
import {mapActions, mapGetters} from 'vuex';
import debounce from '@/mixins/debounce';
import DashboardIcon from '@/components/icons/DashboardIcon.vue';
import CaseIconFilled18 from '@/components/icons/CaseIconFilled18.vue';
import CaseIconHollow18 from '@/components/icons/CaseIconHollow18.vue';
import TeamIconFilled18 from '@/components/icons/TeamIconFilled18.vue';
import TeamIconHollow18 from '@/components/icons/TeamIconHollow18.vue';
import InviteIconFilled18 from '@/components/icons/InviteIconFilled18.vue';
import InviteIconHollow18 from '@/components/icons/InviteIconHollow18.vue';
import PermissionsIconFilled18 from '@/components/icons/PermissionsIconFilled18.vue';
import PermissionsIconHollow18 from '@/components/icons/PermissionsIconHollow18.vue';
import ManagementIconFilled18 from '@/components/icons/ManagementIconFilled18.vue';
import ManagementIconHollow18 from '@/components/icons/ManagementIconHollow18.vue';
import CaseProfileIconFilled18 from '@/components/icons/CaseProfileIconFilled18.vue';
import CaseProfileIconHollow18 from '@/components/icons/CaseProfileIconHollow18.vue';
import MessagesIconFilled18 from '@/components/icons/MessagesIconFilled18.vue';
import MessagesIconHollow18 from '@/components/icons/MessagesIconHollow18.vue';
import FilesIconFilled18 from '@/components/icons/FilesIconFilled18.vue';
import FilesIconHollow18 from '@/components/icons/FilesIconHollow18.vue';
import CaseActionsIconFilled18 from '@/components/icons/CaseActionsIconFilled18.vue';
import CaseActionsIconHollow18 from '@/components/icons/CaseActionsIconHollow18.vue';
import CustodianSupportIconFilled18 from '@/components/icons/CustodianSupportIconFilled18.vue';
import CustodianSupportIconHollow18 from '@/components/icons/CustodianSupportIconHollow18.vue';
import CollapseNavigationIcon18 from '@/components/icons/CollapseNavigationIcon18.vue';

export default {
  name: 'Sidebar',
  components: {
    ManagementIconHollow18,
    ManagementIconFilled18,
    PermissionsIconHollow18,
    PermissionsIconFilled18,
    InviteIconHollow18,
    InviteIconFilled18,
    TeamIconHollow18,
    TeamIconFilled18,
    CaseIconHollow18,
    CaseIconFilled18,
    DashboardIcon,
    CustomCaseSelect,
    AlertErrorMsg,
    LogoIcon,
    CaseProfileIconFilled18,
    CaseProfileIconHollow18,
    MessagesIconFilled18,
    MessagesIconHollow18,
    FilesIconFilled18,
    FilesIconHollow18,
    CaseActionsIconFilled18,
    CaseActionsIconHollow18,
    CustodianSupportIconFilled18,
    CustodianSupportIconHollow18,
    CollapseNavigationIcon18,
  },
  mixins: [debounce],
  data() {
    return {
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters('appState', {
      isSidebarCollapsed: 'getSidebarState',
      currentCase: 'getCurrentCase',
    }),
    notificationsCount() {
      return this.$store.state.notifications.notifications;
    },
  },
  mounted() {
    this.debounce(() =>
      this.$store.dispatch('profile/getPermissionPerCase',
        {caseId: this.currentCase ? this.currentCase.id : this.$route.params.slug}));
    // this.$load(async () => {
    //   this.notificationsCount = (await this.$api.notifications.getPermissionsCount()).data.body.result
    // })
  },
  methods: {
    ...mapActions('appState', {
      setSidebarState: 'setSidebarState',
    }),
    hasPermission,
    isActivePath(input) {
      const paths = Array.isArray(input) ? input : [input];
      return paths.some(path => {
        return this.$route.path.includes(path);
      });
    },
    collapseSidebar(value) {
      if (typeof value === 'boolean') {
        this.setSidebarState(value);
      } else {
        this.setSidebarState(!this.isSidebarCollapsed);
      }
    }
  },
};
</script>

<style scoped>
ul {
  margin: 0 0 32px;
}

.sidebar {
  max-width: 210px;
  width: 100%;
  background-color: var(--new-accent-color);
  min-height: 100vh;
  max-height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  transition: all 0.3s ease-in-out;
}

.holder-logo {
  height: 90px;
  padding: 10px 66.5px 40px;
  cursor: pointer;
  transition: all .3s ease-in-out;
}

.logo {
  display: block;
}

.sidebar-list {
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
}

.sidebar-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-weight: 400;
  font-size: 12px;
  transition: all .2s;
  position: relative;
  height: 42px;
}

.sidebar-list li a {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 15px;
  color: var(--case-btn-border-color);
}

.sidebar-list li a span {
  white-space: nowrap;
}

.sidebar-list li:hover {
  color: var(--secondary-black-color);
  background-color: var(--new-active-accent-color);
  border-left: 3px solid var(--case-btn-border-color);
}

.sidebar-list svg path {
  transition: all .2s;
}

.sidebar-list li a {
  color: var(--case-btn-border-color);
  transition: all .2s;
  text-decoration: none;
}

.sidebar-list li.active {
  border-left: 3px solid var(--case-btn-border-color);
}

.sidebar-list li.active a {
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
}

.sidebar-list li.active {
  background: var(--new-active-accent-color);
}

.sidebar-list li.active >>> svg path {
  fill: var(--case-btn-border-color);
}

.sidebar-list svg {
  min-width: 18px;
  min-height: 18px;
  margin-right: 10px;
}

.sidebar-list button {
  border: none;
  background-color: transparent;
  position: relative;
  margin-right: 20px;
}

.sidebar-list button svg {
  margin-right: 0;
}

.sidebar-list button svg path {
  fill: var(--accent-color);
}

.sidebar-list .notification-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  margin-left: 20px;
  border-radius: 50%;
  color: var(--accent-color);
  background: var(--active-accent-color);
}

.notification-btn span {
  display: block;
  padding: 15px;
}

.case-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 10px 15px;
  color: var(--case-btn-border-color);
}

.case-select {
  display: flex;
  align-items: center;
  gap: 15px;
}

.case-select-icon-wrapper {
  background: var(--white-color);
  border-radius: 50%;
  padding: 5px;
  max-width: 20px;
  max-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.case-select-icon-wrapper svg {
  min-width: 18px;
  min-height: 18px;
}

.case-select-none {
  height: 25px;
  padding: 10px 0;
  gap: 5px;
}

.collapse-wrapper {
  position: absolute;
  bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all .2s;
  width: 100%;
}

.collapse-wrapper svg {
  min-width: 18px;
  min-height: 18px;
}

.collapse-wrapper:hover {
  color: var(--secondary-black-color);
  background-color: var(--new-active-accent-color);
  border-left: 3px solid var(--case-btn-border-color);
}

.collapse-wrapper span {
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: var(--case-btn-border-color);
  white-space: nowrap;
}
</style>
